// Non-component helpers used by OrderReceipt + orderReceiptPdf. Lives in
// its own file because Vite's React Fast Refresh requires component
// modules to export only components; mixing in named utility exports
// breaks HMR for that file and forces full page reloads.

import type { PublisherConfig, SelfServiceSubmissionRow } from '../../types/selfService';
import { resolveDmaName } from './targetingRow';

// CTV-518: shared receipt money rollup for the HTML receipt + PDF. For
// monthly campaigns with live installment rows, totals are sums of the
// backend's per-installment cents (post-extension state) — never recomputed
// locally. Otherwise the submission row's own cents are used. Media spend
// prefers the explicit budget decomposition and falls back to
// total − service fee − tax (clamped at 0 against bad writes).
export interface ReceiptTotals {
  total: number;
  serviceFee: number;
  tax: number;
  mediaSpend: number;
  useLiveSchedule: boolean;
}

export function receiptTotalsFor(row: SelfServiceSubmissionRow): ReceiptTotals {
  const liveInstallments = row.installments ?? [];
  const useLiveSchedule = row.payment_plan === 'monthly' && liveInstallments.length > 0;
  const totalCents = useLiveSchedule
    ? liveInstallments.reduce((s, i) => s + i.amountCents, 0)
    : row.amount_cents ?? 0;
  const feeCents = useLiveSchedule
    ? liveInstallments.reduce((s, i) => s + i.serviceFeeCents, 0)
    : row.service_fee_cents ?? 0;
  const taxCents = useLiveSchedule
    ? liveInstallments.reduce((s, i) => s + (i.taxCents ?? 0), 0)
    : row.tax_cents ?? 0;
  const budgetCents = useLiveSchedule
    ? liveInstallments.reduce(
        (s, i) => s + (i.budgetCents ?? Math.max(0, i.amountCents - i.serviceFeeCents - (i.taxCents ?? 0))),
        0,
      )
    : row.budget_cents ?? Math.max(0, totalCents - feeCents - taxCents);
  return {
    total: totalCents / 100,
    serviceFee: feeCents / 100,
    tax: taxCents / 100,
    mediaSpend: Math.max(0, budgetCents) / 100,
    useLiveSchedule,
  };
}

// Receipt targeting row shared by the HTML receipt + PDF (CTV-674 aligned
// the PDF to this; previously it ignored picked markets for single-market
// publishers). The API parses target_geographies (a comma-separated TEXT
// column written by serializeTargetGeographies) into campaign.targetMarkets,
// a flat list of codes whose format depends on the geo tab the buyer used:
//   - 5-digit codes → ZIPs (radius or manual ZIP tab)
//   - 2-letter codes → US state codes
//   - else (3-digit numeric / mixed) → DMA codes
// We sniff the format the same way the dashboard does and render a concise
// summary. The "Channel: <name>" label is reserved for the legacy /
// default-mode case where target_geographies is empty; once the buyer picks
// ANY codes (including a DMA that happens to match the publisher's own
// default), we show what they picked.
export function receiptTargetingRow(
  row: SelfServiceSubmissionRow,
  config: PublisherConfig,
): { label: string; value: string } {
  const targeted = row.campaign?.targetMarkets ?? [];
  if (targeted.length > 0) {
    const allZip = targeted.every((c) => /^\d{5}$/.test(c));
    if (allZip) {
      return { label: 'Targeting', value: `${targeted.length} ZIP Code${targeted.length === 1 ? '' : 's'}` };
    }
    const allState = targeted.every((c) => /^[A-Z]{2}$/.test(c));
    if (allState) {
      return { label: 'Targeting', value: targeted.join(', ') };
    }
    // Treat the rest as DMA codes; resolve names from the publisher's
    // configured markets first, then the canonical Nielsen list (CTV-674:
    // buyers can target markets beyond the publisher's own coverage).
    const names = targeted
      .map((code) => resolveDmaName(config, code))
      .filter(Boolean);
    return { label: 'Metros', value: names.join(', ') };
  }
  if (config.markets.length === 1) {
    const m = config.markets[0];
    return { label: 'Channel', value: m.channels.join(', ') || m.dmaName || '—' };
  }
  return { label: 'Metros', value: '—' };
}

// Receipt id derived from the submission UUID — short, readable, stable.
// We strip dashes and take the first 8 hex chars so it's easy to read aloud
// over the phone without losing collision-resistance for a single advertiser.
export function receiptNumberFor(submissionId: string): string {
  return `R-${submissionId.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
}

// Stripe gives lowercase card brand slugs ("visa", "amex", "mastercard").
// Title-case them for display, with a handful of well-known abbreviations
// preserved at their conventional casing.
const BRAND_OVERRIDES: Record<string, string> = {
  amex: 'American Express',
  jcb: 'JCB',
  unionpay: 'UnionPay',
  diners: 'Diners Club',
};
export function formatCardBrand(brand: string): string {
  const lower = brand.toLowerCase();
  if (BRAND_OVERRIDES[lower]) return BRAND_OVERRIDES[lower];
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
